/**
 * BulkPriceModal — mass price update across selected catalog systems.
 * Percent (+10%) or absolute (+500₽) delta applied to all v3_price_tiers.
 */
import { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, TrendingUp, DollarSign, CheckSquare, Square, Loader2, AlertCircle } from 'lucide-react';
import { AdminCatalogSystem, bulkPriceUpdate, getAdminSystemsList } from '@/api';
import { useHaptic } from '@/hooks/useHaptic';

interface BulkPriceModalProps {
  open: boolean;
  onClose: () => void;
}

export function BulkPriceModal({ open, onClose }: BulkPriceModalProps) {
  const haptic = useHaptic();
  const [systems, setSystems] = useState<AdminCatalogSystem[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [mode, setMode] = useState<'percent' | 'absolute'>('percent');
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<{ updated_tiers: number } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    getAdminSystemsList()
      .then((data) => setSystems(data))
      .catch(() => setError('Ошибка загрузки систем'))
      .finally(() => setLoading(false));
    setSelected(new Set());
    setValue('');
    setResult(null);
    setError(null);
  }, [open]);

  const filtered = systems.filter((s) =>
    !search || s.title?.toLowerCase().includes(search.toLowerCase()) || s.slug?.includes(search)
  );

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((s) => s.id)));
    }
  };

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const handleSubmit = async () => {
    if (!selected.size || !value) return;
    const num = parseFloat(value);
    if (isNaN(num)) return setError('Введите корректное число');
    setSubmitting(true);
    setError(null);
    try {
      const res = await bulkPriceUpdate({
        system_ids: Array.from(selected),
        mode,
        value: num,
      });
      setResult(res);
      haptic.success();
    } catch {
      setError('Ошибка обновления цен');
      haptic.error();
    } finally {
      setSubmitting(false);
    }
  };

  const previewLabel = () => {
    const num = parseFloat(value);
    if (isNaN(num) || !value) return null;
    if (mode === 'percent') return num > 0 ? `+${num}%` : `${num}%`;
    return num > 0 ? `+${num.toLocaleString('ru')} ₽` : `${num.toLocaleString('ru')} ₽`;
  };

  if (!open) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.96, y: 16 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.96, y: 16 }}
          className="w-full max-w-lg bg-[#111] border border-white/10 rounded-2xl shadow-2xl flex flex-col max-h-[85vh]"
        >
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 shrink-0">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-emerald-500/10">
                <TrendingUp size={16} className="text-emerald-400" />
              </div>
              <div>
                <h2 className="text-sm font-semibold text-white">Массовое изменение цен</h2>
                <p className="text-xs text-white/40">Применяется ко всем тирам выбранных систем</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-1.5 rounded-lg hover:bg-white/10 text-white/40 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>

          {/* Controls */}
          <div className="px-5 py-4 space-y-3 border-b border-white/10 shrink-0">
            {/* Mode toggle */}
            <div className="flex gap-2">
              <button
                onClick={() => setMode('percent')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-all border ${
                  mode === 'percent'
                    ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-400'
                    : 'bg-white/[0.03] border-white/10 text-white/50 hover:text-white/70'
                }`}
              >
                <TrendingUp size={14} />
                Процент
              </button>
              <button
                onClick={() => setMode('absolute')}
                className={`flex-1 flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-medium transition-all border ${
                  mode === 'absolute'
                    ? 'bg-blue-500/15 border-blue-500/40 text-blue-400'
                    : 'bg-white/[0.03] border-white/10 text-white/50 hover:text-white/70'
                }`}
              >
                <DollarSign size={14} />
                Абсолютное
              </button>
            </div>

            {/* Value input */}
            <div className="relative">
              <input
                type="number"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={mode === 'percent' ? 'Например: 10 (= +10%)' : 'Например: 500 (= +500₽)'}
                className="w-full px-4 py-2.5 rounded-xl bg-white/[0.05] border border-white/10 text-sm text-white placeholder-white/30 focus:outline-none focus:border-emerald-500/50 pr-16"
              />
              {previewLabel() && (
                <span className={`absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold ${
                  parseFloat(value) >= 0 ? 'text-emerald-400' : 'text-red-400'
                }`}>
                  {previewLabel()}
                </span>
              )}
            </div>

            {/* Search */}
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Фильтр систем..."
              className="w-full px-4 py-2 rounded-xl bg-white/[0.03] border border-white/10 text-xs text-white placeholder-white/30 focus:outline-none"
            />
          </div>

          {/* Systems list */}
          <div className="flex-1 overflow-y-auto px-5 py-3 space-y-1 min-h-0">
            {loading ? (
              <div className="flex items-center justify-center py-8 text-white/40">
                <Loader2 size={18} className="animate-spin mr-2" />
                Загрузка...
              </div>
            ) : (
              <>
                {/* Select all */}
                <button
                  onClick={toggleAll}
                  className="flex items-center gap-2 w-full px-3 py-2 rounded-lg hover:bg-white/5 transition-colors text-xs text-white/50 hover:text-white/80"
                >
                  {selected.size === filtered.length && filtered.length > 0 ? (
                    <CheckSquare size={14} className="text-emerald-400" />
                  ) : (
                    <Square size={14} />
                  )}
                  {selected.size === filtered.length && filtered.length > 0 ? 'Снять всё' : 'Выбрать всё'}
                  <span className="ml-auto text-white/30">{filtered.length} систем</span>
                </button>

                {filtered.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => toggle(s.id)}
                    className={`flex items-center gap-2.5 w-full px-3 py-2.5 rounded-xl transition-all text-left border ${
                      selected.has(s.id)
                        ? 'bg-emerald-500/10 border-emerald-500/20'
                        : 'bg-white/[0.02] border-transparent hover:bg-white/[0.04]'
                    }`}
                  >
                    {selected.has(s.id) ? (
                      <CheckSquare size={14} className="text-emerald-400 shrink-0" />
                    ) : (
                      <Square size={14} className="text-white/30 shrink-0" />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white/90 truncate">{s.title}</p>
                      <p className="text-[11px] text-white/40">{s.slug} · {s.category}</p>
                    </div>
                  </button>
                ))}
              </>
            )}
          </div>

          {/* Footer */}
          <div className="px-5 py-4 border-t border-white/10 shrink-0 space-y-3">
            {error && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-red-500/10 text-red-400 text-xs">
                <AlertCircle size={13} />
                {error}
              </div>
            )}
            {result && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 text-emerald-400 text-xs">
                ✓ Обновлено {result.updated_tiers} тиров у {selected.size} систем
              </div>
            )}
            <div className="flex items-center gap-3">
              <span className="text-xs text-white/40">
                Выбрано: <span className="text-white/70 font-medium">{selected.size}</span> систем
              </span>
              <button
                onClick={handleSubmit}
                disabled={!selected.size || !value || submitting || !!result}
                className="ml-auto flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium transition-all bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <TrendingUp size={14} />}
                {submitting ? 'Применяю...' : 'Применить'}
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
